<template>
  <div id="uploadView">
    <form-wizard
      disable-back-navigation
      :start-index.sync="activeTabIndex"
      loading-icon="spinner-border spinner-border-sm"
      :loading.sync="loading"
      @on-complete="onComplete"
      @on-loading="setLoading"
    >
      <component
        :is="tab.component || UpdateQuestion"
        v-for="(tab, index) in filteredTabs"
        :key="index"
      />
    </form-wizard>
  </div>
</template>

<script lang="ts">
import Vue from 'vue'
import Component from 'vue-class-component'

import UploadData from '@/components/admin/upload/UploadData.vue'
import CSVSettings from '@/components/admin/upload/CSVSettings/CSVSettings.vue'
import UploadShp from '@/components/admin/upload/UploadShp.vue'
import ShpSettings from '@/components/admin/upload/ShpSettings/ShpSettings.vue'
import UpdateQuestion from '@/components/admin/upload/UpdateQuestion.vue'
import {
  postVersionStatus,
  postConfig,
} from '@/api'
import {
  VersionStatuses,
  StoryKeys,
} from '@/types'
import {
  SHOW_MAP_CONFIG_KEY,
  ROUTE_BUILDER_PRIMARY,
  ROUTE_SIDEBAR_SETTINGS,
  VIEW_PROJECTS,
  AUTO_TOTAL_FIELD_COLUMN,
  AUTO_TOTAL_FIELD_NAME,
  DEFAULT_STORY_KEY,
  PATH_ROOT,
} from '@/constants'
import { useUploadStore } from '@/store/admin/upload'
import _range from 'lodash/range'

const components = {
  UpdateQuestion,
  UploadData,
  CSVSettings,
  UploadShp,
  ShpSettings,
}

@Component({ components })
export default class UploadView extends Vue {
  UpdateQuestion = UpdateQuestion
  activeTabIndex = 0
  loading = false
  showMap = true
  upload = useUploadStore()

  mounted () {
    this.upload.resetAllUploadData()
    if ('showMap' in this.$route.query) {
      this.showMap = this.$route.query.showMap === 'true'
    }
  }

  get tabs () {
    return [
      { component: UploadData },
      { component: CSVSettings },
      { component: UploadShp, mapOnly: true },
      { component: ShpSettings, mapOnly: true },
    ]
  }

  get filteredTabs () {
    return this.tabs.filter(tab => tab.mapOnly ? this.showMap : true)
  }

  get defaultStoryNumbers () {
    return this.$typedStore.visualisationData.allLevels
  }

  get defaultStoryKeys () {
    return this.defaultStoryNumbers.map((i) => {
      const column = this.upload.levelsHelper.fieldForLevel(i)?.column
      const story = this.defaultLevelStories(i)

      return {
        path: `${DEFAULT_STORY_KEY}${i}`,
        level: i,
        column,
        story,
      }
    })
  }

  get defaultLevelStories () {
    return (level:number) => {
      const levels = _range(1, level + 1)
      const fields = levels.map(i => this.upload.levelsHelper.fieldForLevel(i)?.column)
      const stories = (l:number) =>
        l === 1 ? `<h1><span data-mention="" class="mention" data-mention-id="${fields[0]}">@${fields[0]}</span></h1>`
          : l === 2 ? `<h3><span data-mention="" class="mention" data-mention-id="${fields[1]}">@${fields[1]}</span></h3>`
            : `<p><u><span data-mention="" class="mention" data-mention-id="${fields[l - 1]}">@${fields[l - 1]}</span></u></p>`

      return levels.map(l => stories(l)).join('')
    }
  }

  async generateDefaultLevelStories () {
    await this.$typedStore.primary.entities.nodes.updateNodeValue({
      path: PATH_ROOT,
      key: StoryKeys.SIDEBAR_TOP,
      value: `<h1>${this.$typedStore.primary.app.appName}</h1>`,
    })

    await this.$typedStore.primary.entities.nodes.updateNodeValue({
      path: PATH_ROOT,
      key: StoryKeys.SIDEBAR_BOT,
      value: `<p>Total: <span data-mention="" class="mention" data-mention-id="${AUTO_TOTAL_FIELD_COLUMN}">@${AUTO_TOTAL_FIELD_NAME}</span></p>`,
    })

    await Promise.all(this.defaultStoryKeys.map(async ({ path, story }) => {
      await this.$typedStore.primary.entities.nodes.updateNodeValue({
        path,
        key: StoryKeys.SIDEBAR_TOP,
        value: story,
      })
    }))

    const saveResult = await this.$typedStore.primary.entities.nodes.saveNodeUpdates()
    return saveResult === 'success'
  }

  async onComplete () {
  // Persist loading=true state so finish button remains disabled
    this.setLoading(true)
    // Set config for show map or not
    await this.setShowMapConfig()
    // Change version status to built
    await postVersionStatus(VersionStatuses.BUILT)
    // TODO: handle if it returns false AKA if it fails
    await this.generateDefaultLevelStories()
    // Set loaded to false to force download latest data when navigation occurs
    this.$typedStore.primary.app.setAppLoaded(false)
    // Navigate to the builder view to see the uploaded app
    this.$router.push({
      name: ROUTE_BUILDER_PRIMARY,
      params: {
        sidepanel: ROUTE_SIDEBAR_SETTINGS,
        mainpanel: VIEW_PROJECTS,
      },
      query: { showSetupWizard: 'true' },
    })
  }

  setLoading (value: boolean) {
    this.loading = value
  }

  async setShowMapConfig () {
    await postConfig({
      key: SHOW_MAP_CONFIG_KEY,
      value: this.showMap,
    })
  }
}

</script>
